import React from 'react';
import type { JSX } from 'react';
import * as RadioGroup from '@radix-ui/react-radio-group';
import './RadioButton.scss';
import { trackEvent } from "@/components/atoms";

interface RadioButtonProps {
  /** Array of options for the radio group */
  options: Record<string, string>;
  /** Currently selected value */
  value: string;
  /** Function executed when selection changes */
  onChange: (value: string) => void;
  /** Optional additional CSS class */
  className?: string;
  /** Button variant */
  variant?: 'primary' | 'secondary';
  /** Padding and font density tier */
  density?: 'compact' | 'default' | 'spacious';
  /** Hover/active transform intensity (independent of density) */
  motionScale?: 'sm' | 'base' | 'lg';
  disabled?: boolean;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
}

/**
 * Atomic radio button group component
 * @component
 * @param {RadioButtonProps} props - Component props
 * @returns {JSX.Element} RadioButton component
 */
const RadioButton: React.FC<RadioButtonProps> = ({
  options,
  value,
  onChange,
  className = '',
  variant = 'primary',
  density = 'default',
  motionScale = 'base',
  disabled = false,
  "aria-labelledby": ariaLabelledBy,
  "aria-describedby": ariaDescribedBy,
}): JSX.Element => {
  const handleValueChange = (newValue: string) => {
    trackEvent('User Interaction', `select_radio_${newValue}`, options[newValue]);
    onChange(newValue);
  };

  return (
    <RadioGroup.Root
      value={value}
      onValueChange={handleValueChange}
      disabled={disabled}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      className={`radio-button ${className}`}
    >
      {Object.entries(options).map(([key, label]) => (
        <RadioGroup.Item
          key={key}
          value={key}
          className={`radio-button__item radio-button__item--${density} radio-button__item--${variant} radio-button__item--linear radio-button__item--motion-${motionScale}`}
        >
          {label}
        </RadioGroup.Item>
      ))}
    </RadioGroup.Root>
  );
};

export default RadioButton; 